import { match } from 'path-to-regexp';

import type { BulletinBoard } from '@autopass-web/types';
import type { LicensePlate, Parkinglot } from '@autopass-web/types';

type ReplaceRuleRecord = Record<
  string,
  {
    shouldReplace?: boolean | ((board: BulletinBoard) => boolean);
    value: ((board: BulletinBoard) => string) | string;
  }
>;

const replaceBulletinLink = (
  board: BulletinBoard,
  rulesRecord: ReplaceRuleRecord,
) => {
  // only support bulletin action type: open_link
  if (!board?.action?.open_link?.uri) {
    return board;
  }

  const transedUrl = Object.entries(rulesRecord).reduce(
    (url, [placeHolder, rule]) => {
      const shouldReplace =
        typeof rule.shouldReplace === 'function'
          ? rule.shouldReplace(board)
          : (rule?.shouldReplace ?? true);

      if (!shouldReplace) return url;

      const value =
        typeof rule.value === 'function' ? rule.value(board) : rule.value;

      const transed = url.replace(`{${placeHolder}}`, value);

      return transed;
    },
    board.action.open_link.uri,
  );

  board.action.open_link.uri = transedUrl;

  return board;
};

const PARKINGLOT_INFO_BOARD_KEY = 'should_carry_parkinglot_code';

export const createParkinglotCodeReplacer = (
  parkinglot: Parkinglot,
): ((bulletin: BulletinBoard) => BulletinBoard) => {
  return (bulletin) => {
    const parkingLotUrl = parkinglot.action_link.link;
    const parkinglotUrlPath = '/' + parkingLotUrl.replace(/.*?\//, '');

    const pathMatch = match<{ p: string; c: string }>('/p/:p/c/:c')(
      parkinglotUrlPath,
    );

    const c = pathMatch ? pathMatch.params.c : '';
    const p = pathMatch ? pathMatch.params.p : '';

    const shouldReplace =
      !!pathMatch && !!bulletin.attributes[PARKINGLOT_INFO_BOARD_KEY];

    return replaceBulletinLink(bulletin, {
      p: {
        shouldReplace,
        value: p,
      },
      c: {
        shouldReplace,
        value: c,
      },
    });
  };
};

const CARRY_CAR_INFO_BOARD_KEY = 'should_carry_car_info';

export const createVehicleInfoReplacer = (
  plateNumber: LicensePlate['plate_number'],
  vehicleType: LicensePlate['vehicle_type'],
): ((bulletin: BulletinBoard) => BulletinBoard) => {
  return (board) => {
    return replaceBulletinLink(board, {
      plate: {
        shouldReplace: !!board.attributes[CARRY_CAR_INFO_BOARD_KEY],
        value: plateNumber,
      },
      type: {
        shouldReplace: !!board.attributes[CARRY_CAR_INFO_BOARD_KEY],
        value: vehicleType,
      },
    });
  };
};
